/**
 * A Resource's free NOS hours over a Plan Line's months — the figure the Resource picker ranks
 * by after Fit Score, and a line's Candidates show (decision 2026-10-19). NOS capacity − NOS
 * guidance hours, the same source as the grid's load bars: summed for a Total plan, the next
 * three months averaged for a Monthly one.
 *
 * "86 hrs free · 394 of 480 guided" in DM Mono, plus a strip with one tick per month so a
 * tight month is visible inside a healthy total.
 */

import { planStatusLabel } from "@/lib/plan-status-labels";
import { hoursLabel, monthLabel, windowLabel } from "@/lib/resource-availability";
import { cn } from "@/lib/utils";
import type {
  ResourceAvailabilityView,
  ResourcePickerOtherPlanView,
} from "@/schemas/resource-plans";

function hrs(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function monthsTooltip(a: ResourceAvailabilityView): string {
  const basis =
    a.basis === "Monthly"
      ? "Hours free a month = NOS capacity − NOS guidance, averaged over the next three months."
      : "Hours free = NOS capacity − NOS guidance, added up over this line's months from now on.";
  const lines = a.months.map(
    (m) =>
      `${monthLabel(m.month)}: ${hrs(m.guidanceHours)} guided of ${hrs(m.capacityHours)} → ${hrs(m.free)} free`,
  );
  const missing =
    a.missingMonths.length === 0
      ? ""
      : `\nNOS has no hours yet for ${windowLabel(a.missingMonths)} — not counted.`;
  return `${basis} Suggested and Candidate mentions on other plans book nothing and are not subtracted.\n${lines.join("\n")}${missing}`;
}

function tone(free: number): string {
  return free > 0
    ? "text-[var(--fit-strong)]"
    : free === 0
      ? "text-[var(--fit-moderate)]"
      : "text-[var(--fit-at-risk)]";
}

export function AvailabilitySummary({
  availability: a,
}: {
  availability: ResourceAvailabilityView;
}) {
  if (a.freeHours === null) {
    return (
      <span
        className="cursor-help font-mono text-xs text-muted-foreground"
        title="NOS has no capacity or guidance hours for this Resource in these months."
      >
        No NOS hours
      </span>
    );
  }
  return (
    <span className="inline-flex cursor-help items-center gap-1.5" title={monthsTooltip(a)}>
      <span className="font-mono text-xs">
        <span className={cn("font-semibold", tone(a.freeHours))}>
          {hoursLabel(a.freeHours, a.basis)} free
        </span>
        {a.guidanceHours !== null && a.capacityHours !== null && (
          <span className="text-muted-foreground">
            {" "}
            · {hrs(a.guidanceHours)} of {hrs(a.capacityHours)} guided
          </span>
        )}
      </span>
      {a.months.length > 1 && (
        <span aria-hidden className="inline-flex items-end gap-px">
          {a.months.map((m) => (
            <span
              key={m.month}
              className={cn(
                "h-2 w-1 rounded-sm",
                m.free > 0
                  ? "bg-[var(--fit-strong)]"
                  : m.free === 0
                    ? "bg-[var(--fit-moderate)]"
                    : "bg-[var(--fit-at-risk)]",
              )}
            />
          ))}
        </span>
      )}
    </span>
  );
}

export function OtherPlansPill({
  plans,
  count,
}: {
  plans: ResourcePickerOtherPlanView[];
  count: number;
}) {
  if (count === 0) return null;
  const listed = plans.map((p) => `${p.name} (${planStatusLabel(p.status)}, ${p.kind})`).join("\n");
  const more = count > plans.length ? `\n…and ${count - plans.length} more` : "";
  return (
    <span
      className="cursor-help rounded bg-muted px-1 text-xs text-muted-foreground"
      title={`Also named on other open plans whose months overlap — books no capacity:\n${listed}${more}`}
    >
      on {count} other plan{count === 1 ? "" : "s"}
    </span>
  );
}
